import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowRight, ArrowLeft } from "lucide-react";
import { useCart } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import useWishlist from "@/hooks/useWishlist";

export default function WishlistPage() {
  const { products, loading } = useCart();
  const { ids, clear } = useWishlist();

  // Map stored ids → live product objects (skipping any that have been deleted).
  const items = useMemo(() => {
    if (!ids.length || !products.length) return [];
    const byId = new Map(products.map((p) => [p.id, p]));
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }, [ids, products]);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="wishlist-page">
      <Header />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-slate-600 hover:text-emerald-600 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to shop
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center">
                <Heart className="h-4 w-4 text-rose-600 fill-rose-500" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
                Your Wishlist
              </h1>
            </div>
            <p className="text-slate-500">
              {items.length > 0
                ? `${items.length} saved item${items.length === 1 ? "" : "s"}`
                : "Save items you love by tapping the heart on any product."}
            </p>
          </div>
          {items.length > 0 && (
            <Button
              onClick={() => {
                if (window.confirm("Remove all items from your wishlist?")) clear();
              }}
              variant="outline"
              className="border-rose-200 text-rose-600 hover:bg-rose-50 rounded-full"
              data-testid="wishlist-clear-btn"
            >
              Clear wishlist
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : items.length === 0 ? (
          <div
            className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center"
            data-testid="wishlist-empty"
          >
            <div className="w-14 h-14 rounded-full bg-white shadow-sm mx-auto flex items-center justify-center mb-4">
              <Heart className="h-6 w-6 text-slate-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-1">
              No favourites yet
            </h2>
            <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
              Tap the heart icon on any product card and it will land here so
              you can find it again quickly.
            </p>
            <Link to="/products">
              <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-full">
                Browse products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            data-testid="wishlist-grid"
          >
            {items.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
